import React, { useEffect, useState } from 'react';
import '../styles/auth-theme.css';

function requireViteEnv(name: string): string {
  const val = import.meta.env[name] as string | undefined;
  if (!val) {
    throw new Error(`Missing required env var: ${name}`);
  }
  return val;
}

function getDeviceName(): string {
  const parts = navigator.userAgent.split(/[()]/);
  const devicePart = parts[1];
  if (!devicePart) {
    throw new Error('Could not parse device name from User-Agent');
  }
  return devicePart;
}

interface CliLoginCallbackProps {
  idToken: string;
  port: number;
  state: string;
}

export function CliLoginCallback({ idToken, port, state }: CliLoginCallbackProps): React.JSX.Element {
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (port < 1024 || port > 65535) {
      setError(`Invalid port: ${port}. Must be between 1024 and 65535.`);
      return;
    }

    const apiUrl = requireViteEnv('VITE_API_URL');
    let deviceName: string;
    try {
      deviceName = getDeviceName();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to determine device name';
      setError(message);
      return;
    }

    void createSessionAndRedirect(apiUrl, deviceName);
  }, [idToken, port, state]);

  async function createSessionAndRedirect(apiUrl: string, deviceName: string): Promise<void> {
    try {
      const response = await fetch(`${apiUrl}/cli-sessions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ deviceName }),
      });

      if (!response.ok) {
        const body = await response.text();
        setError(`CLI session creation failed (${response.status}): ${body}`);
        return;
      }

      const data = (await response.json()) as { sessionToken: string; entityId: string };
      window.location.assign(
        `http://localhost:${port}/callback?token=${data.sessionToken}&state=${state}&entityId=${data.entityId}`,
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(`CLI session creation failed: ${message}`);
    }
  }

  if (error) {
    return (
      <div className="auth-page">
        <img className="auth-watermark" src="/synphant-badge.png" alt="" />
        <div className="auth-card">
          <div className="auth-brand">
            <div className="auth-wordmark">
              <span className="w-syn">Syn</span>
              <span className="w-phant">phant</span>
            </div>
          </div>
          <h1 className="auth-title">CLI Login</h1>
          <p className="auth-error">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <img className="auth-watermark" src="/synphant-badge.png" alt="" />
      <div className="auth-card">
        <div className="auth-brand">
          <div className="auth-wordmark">
            <span className="w-syn">Syn</span>
            <span className="w-phant">phant</span>
          </div>
        </div>
        <h1 className="auth-title">CLI Login</h1>
        <p className="auth-subtitle">Creating CLI session...</p>
      </div>
    </div>
  );
}
